import { useEffect, useRef, useState } from "react";
import { CheckCircle2, ExternalLink, MonitorPlay, Play, Square } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import ComponentCard from "../../../../../shared/ui/common/ComponentCard";
import Button from "../../../../../shared/ui/button/Button";
import { isAiConsentRequired, uploadSessionAudio } from "../../../application/uploadSessionAudio";
import { LoadingOverlay } from "../../../../../shared/ui/common/LoadingOverlay";
import { VideoCallRecorder } from "../../../../../services/video-call-recorder";
import { useRecordingActivity } from "../../../../auth";
import { useBreakpoint } from "../../../../../hooks/useBreakpoint";
import { LAYOUT } from "../../../../../shared/layout";
import {
  MIN_SESSION_AUDIO_MINUTES,
  MIN_SESSION_AUDIO_SECONDS,
  formatDuration,
} from "../../../domain/audioMinimum";

interface Props {
  /** Where "finish later" goes: the sessions of the record this one belongs to. */
  exitPath: string;
  sessionId: string;
  meetingLink?: string | null;
  onContinue: () => void;
  onBack: () => void;
}

const MIN_RECORDING_SECONDS = MIN_SESSION_AUDIO_SECONDS;

export default function SessionAddVideoCallForm({
  sessionId,
  exitPath,
  meetingLink,
  onContinue,
  onBack,
}: Props) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isMobile } = useBreakpoint();
  const { setRecordingActive } = useRecordingActivity();

  const recorderRef = useRef<VideoCallRecorder | null>(null);
  const timerRef = useRef<number | null>(null);
  const unmountedRef = useRef(false);

  const [recording, setRecording] = useState(false);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  const supported = VideoCallRecorder.isSupported();

  useEffect(() => {
    if (!recording) return;
    setRecordingActive(true);
    return () => setRecordingActive(false);
  }, [recording, setRecordingActive]);

  useEffect(() => {
    unmountedRef.current = false;
    return () => {
      unmountedRef.current = true;
      stopTimer();
      if (recorderRef.current?.isActive()) {
        recorderRef.current.stop().catch(() => {});
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startTimer = () => {
    if (timerRef.current !== null) return;
    timerRef.current = window.setInterval(() => {
      setElapsedTime((prev) => prev + 1);
    }, 1000);
  };

  const stopTimer = () => {
    if (timerRef.current !== null) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60).toString().padStart(2, "0");
    const secs = (seconds % 60).toString().padStart(2, "0");
    return `${mins}:${secs}`;
  };

  const startRecording = async () => {
    if (recording) return;
    setError(null);

    if (recorderRef.current?.isPaused()) {
      recorderRef.current.resume();
      setRecording(true);
      startTimer();
      return;
    }

    const recorder = new VideoCallRecorder();
    try {
      await recorder.start();
    } catch (err) {
      // Release anything the failed start left open (shared tab, mic).
      recorder.stop().catch(() => {});
      // User cancelled the picker or denied mic access — not an error worth
      // showing unless it was a genuine failure.
      if (err instanceof Error && err.name !== "NotAllowedError") {
        setError(t("session.videoCall.errorCapture"));
      }
      return;
    }

    // The form closed while the picker was open: nobody owns this capture.
    if (unmountedRef.current) {
      recorder.stop().catch(() => {});
      return;
    }

    recorderRef.current = recorder;
    setRecording(true);
    startTimer();
  };

  const stopRecording = () => {
    recorderRef.current?.pause();
    setRecording(false);
    stopTimer();
  };

  const handleContinue = async () => {
    if (recording) stopRecording();

    if (elapsedTime < MIN_RECORDING_SECONDS) {
      setError(
        t("session.live.errorTooShort", { minutes: MIN_SESSION_AUDIO_MINUTES })
      );
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const recorder = recorderRef.current;
      recorderRef.current = null;
      await recorder?.stop();
      const audioBlob = recorder?.getRecordedAudio() ?? null;

      if (audioBlob && audioBlob.size > 0) {
        const contentType = audioBlob.type || "audio/webm";

        // The capture ran as long as the call did; that replaces the planned length.
        await uploadSessionAudio(sessionId, audioBlob, contentType, elapsedTime);

        setIsProcessing(true);
      } else {
        onContinue();
      }
    } catch (err) {
      console.error("Error during video call session finalization:", err);
      if (isAiConsentRequired(err)) {
        setError(t("patient.portalConsent.aiBlocked"));
      } else {
        setError(t("session.live.errorGen"));
      }
    } finally {
      setLoading(false);
    }
  };

  if (isProcessing) {
    return (
      <ComponentCard
        title={t("session.audio.processingTitle", "Audio is being processed")}
      >
        <div className="flex flex-col items-center justify-center p-8 space-y-6 text-center">
          <div className="w-16 h-16 bg-accent-tint rounded-full flex items-center justify-center mb-2">
            <CheckCircle2 className="w-8 h-8 text-accent-text" />
          </div>
          <h3 className="text-xl font-bold text-gray-900">
            {t(
              "session.audio.processingHeader",
              "Your audio is processing in the background"
            )}
          </h3>
          <p className="text-gray-600 max-w-md">
            {t(
              "session.audio.processingDescription",
              "We've safely queued your audio. Scribe will transcribe the audio, generate clinical notes, and run the AI analysis. We'll notify you as soon as it's ready!"
            )}
          </p>
          <div className="pt-6 w-full max-w-xs">
            <Button className="w-full" onClick={() => navigate(exitPath)}>
              {t("session.audio.goToDashboard", "Go to Dashboard")}
            </Button>
          </div>
        </div>
      </ComponentCard>
    );
  }

  if (!supported) {
    return (
      <ComponentCard title={t("session.videoCall.title")}>
        <div className="flex flex-col items-center justify-center p-8 space-y-4 text-center">
          <MonitorPlay aria-hidden="true" className="size-12 text-content-tertiary" />
          <p className="text-gray-600 max-w-md">
            {t(
              "session.videoCall.unsupported",
              "Tab audio capture requires Chrome or Edge. Please switch browsers to use this feature."
            )}
          </p>
          <Button variant="outline" onClick={onBack}>
            {t("session.back")}
          </Button>
        </div>
      </ComponentCard>
    );
  }

  return (
    <div
      className={`flex w-full ${isMobile ? "flex-col" : "gap-6"}`}
      style={{ paddingBottom: isMobile ? LAYOUT.SAFE_AREA_PADDING : 0 }}
    >
      <div
        className={`flex-1 rounded-2xl border ${isMobile ? "p-6" : "p-8"} flex flex-col space-y-8 bg-white`}
      >
        <div className="flex flex-col items-center space-y-4">
          <h3 className="font-semibold text-lg">
            {t("session.videoCall.title")}
          </h3>

          {/* Meeting link */}
          {meetingLink && !recording && elapsedTime === 0 && (
            <a
              href={meetingLink}
              target="_blank"
              rel="noreferrer"
              className="flex items-center gap-2 px-4 py-2 rounded-xl border border-brand-400 text-brand-600 text-sm font-semibold hover:bg-brand-50 transition-colors"
            >
              <ExternalLink className="w-4 h-4" />
              {t("session.videoCall.openMeetingLink")}
            </a>
          )}

          {/* Instructions before recording starts */}
          {elapsedTime === 0 && !recording && (
            <p className="text-sm text-gray-500 text-center max-w-sm">
              {t(
                "session.videoCall.instructions",
                "Start the tab capture, then select the browser tab where your video call is running."
              )}
            </p>
          )}

          {/* Visual indicator */}
          <div
            className={`relative flex items-center justify-center ${isMobile ? "w-36 h-36" : "w-44 h-44"}`}
          >
            {recording && (
              <div className="absolute inset-0 rounded-full bg-purple-100 animate-pulse opacity-40" />
            )}
            <div
              className={`relative w-32 h-32 rounded-full flex items-center justify-center transition-all duration-300 ${
                recording ? "bg-purple-50 shadow-inner" : "bg-slate-100"
              }`}
            >
              <MonitorPlay
                className="w-14 h-14 transition-colors duration-200"
                style={{ color: recording ? "#8A49D6" : "#94a3b8" }}
              />
            </div>
          </div>

          {/* Timer */}
          <div
            className={`flex flex-col items-center transition-colors duration-300 text-gray-600`}
          >
            <p className="text-sm font-mono text-xl font-bold tracking-wider">
              {formatTime(elapsedTime)}
            </p>
          </div>

          {/* Controls */}
          <div className="flex flex-col items-center gap-4 pt-2">
            {!recording ? (
              <button
                onClick={startRecording}
                className="flex items-center gap-2 rounded-full bg-accent px-8 py-4 font-semibold text-accent-contrast transition-colors duration-[--duration-fast] hover:bg-accent-hover active:scale-[0.98]"
                style={{ minHeight: LAYOUT.TOUCH_TARGET_SIZE, minWidth: "200px" }}
              >
                <Play className="w-6 h-6 fill-current" />
                {elapsedTime > 0
                  ? t("session.live.startRecording", "Resume")
                  : t("session.videoCall.startCapture")}
              </button>
            ) : (
              <button
                onClick={stopRecording}
                className="flex items-center gap-2 rounded-full bg-status-critical-fill px-8 py-4 font-semibold text-content-inverse transition-colors duration-[--duration-fast] hover:bg-status-critical-fill/90 active:scale-[0.98]"
                style={{ minHeight: LAYOUT.TOUCH_TARGET_SIZE, minWidth: "200px" }}
              >
                <span aria-hidden="true" className="relative flex size-2.5">
                  <span className="absolute inline-flex size-full animate-ping rounded-full bg-content-inverse opacity-60" />
                  <span className="relative inline-flex size-2.5 rounded-full bg-content-inverse" />
                </span>
                <Square className="size-5 fill-current" />
                {t("session.live.stopRecording", "Stop Recording")}
              </button>
            )}

            {/* Said before the therapist starts sharing, not after they stop
                with too little to work with. */}
            <p className="max-w-md text-center text-sm text-content-secondary">
              {t("session.minAudio.notice", { minutes: MIN_SESSION_AUDIO_MINUTES })}
            </p>

            {elapsedTime > 0 && elapsedTime < MIN_RECORDING_SECONDS && !recording && (
              <p className="text-sm text-content-secondary">
                {t("session.live.minDurationHint", {
                  remaining: formatDuration(MIN_RECORDING_SECONDS - elapsedTime),
                  minutes: MIN_SESSION_AUDIO_MINUTES,
                })}
              </p>
            )}
          </div>
        </div>

        {error && <p className="text-red-500 text-center">{error}</p>}

        <div
          className={`flex justify-center gap-4 pt-6 mt-auto border-t ${isMobile ? "flex-col" : ""}`}
        >
          <button
            onClick={onBack}
            className={`px-4 py-2 rounded-xl border text-sm font-semibold transition-colors hover:bg-gray-50 ${
              isMobile ? "order-2 w-full text-gray-500" : ""
            }`}
            style={isMobile ? { minHeight: LAYOUT.TOUCH_TARGET_SIZE } : {}}
          >
            {t("session.back")}
          </button>
          <button
            onClick={handleContinue}
            disabled={recording || elapsedTime < MIN_RECORDING_SECONDS || loading}
            className={`px-6 py-2 rounded-xl bg-accent text-white text-sm font-semibold shadow-md transition-all active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed ${
              isMobile ? "order-1 w-full py-3 text-base shadow-raised" : ""
            }`}
            style={isMobile ? { minHeight: LAYOUT.TOUCH_TARGET_SIZE } : {}}
          >
            {t("session.continue")}
          </button>
        </div>

        <LoadingOverlay visible={loading} message={t("session.live.processing")} />
      </div>
    </div>
  );
}
